// app/src/dashboard/widgets/ChartWidget.tsx
import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { getHistoricalPriceData, useQuery } from "wasp/client/operations";
import { Card } from "../../client/components/ui/card";
import useTranslation from "../../client/hooks/useTranslation";
import { useDashboardData } from "./DashboardDataContext";
import { ChartToolbar } from "./chart/ChartToolbar";
import { useChartEngine } from "./chart/useChartEngine";
import { useTradeMarkers } from "./chart/useTradeMarkers";
import ChartErrorBoundary from "./chart/ChartErrorBoundary";
import {
  DEFAULT_CHART_CONFIG,
  TIMEFRAME_WINDOW_DAYS,
  type ChartWidgetConfig,
  type Timeframe,
} from "./chart/chartTypes";

interface ChartWidgetProps {
  slotId: string;
  defaultFilterValue?: string | null;
  onUpdateSlotConfig: (slotId: string, value: string) => void;
}

function parseConfig(raw: string | null | undefined): ChartWidgetConfig {
  if (!raw) return { ...DEFAULT_CHART_CONFIG };
  try {
    const parsed = JSON.parse(raw);
    // Migrate legacy field name
    if ("symbolLockedByUser" in parsed && !("locked" in parsed)) {
      parsed.locked = parsed.symbolLockedByUser;
    }
    return { ...DEFAULT_CHART_CONFIG, ...parsed };
  } catch {
    return { ...DEFAULT_CHART_CONFIG };
  }
}

function getMostTradedSymbol(trades: any[]): string {
  if (!trades || trades.length === 0) return "";
  const counts: Record<string, number> = {};
  for (const t of trades) {
    if (t.symbol) counts[t.symbol] = (counts[t.symbol] ?? 0) + 1;
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
}

export default function ChartWidget({
  slotId,
  defaultFilterValue,
  onUpdateSlotConfig,
}: ChartWidgetProps) {
  const t = useTranslation();
  const { trades, isDark, themeId } = useDashboardData();

  const [config, setConfig] = useState<ChartWidgetConfig>(() =>
    parseConfig(defaultFilterValue)
  );

  // Auto-update symbol from most-traded when not locked
  useEffect(() => {
    if (!config.locked) {
      const mostTraded = getMostTradedSymbol(trades);
      if (mostTraded && mostTraded !== config.symbol) {
        setConfig((prev) => ({ ...prev, symbol: mostTraded }));
      }
    }
  }, [trades, config.locked]);

  const persistConfig = useCallback(
    (next: ChartWidgetConfig) => {
      onUpdateSlotConfig(slotId, JSON.stringify(next));
    },
    [slotId, onUpdateSlotConfig]
  );

  const handleSymbolChange = useCallback(
    (symbol: string) => {
      const next = { ...config, symbol, locked: true };
      setConfig(next);
      persistConfig(next);
    },
    [config, persistConfig]
  );

  const handleToggleLock = useCallback(() => {
    const next = { ...config, locked: !config.locked };
    setConfig(next);
    persistConfig(next);
  }, [config, persistConfig]);

  const handleTimeframeChange = useCallback(
    (timeframe: Timeframe) => {
      const next = { ...config, timeframe };
      setConfig(next);
      persistConfig(next);
    },
    [config, persistConfig]
  );

  const handleToggleTrades = useCallback(() => {
    const next = { ...config, showTrades: !config.showTrades };
    setConfig(next);
    persistConfig(next);
  }, [config, persistConfig]);

  // Date window for the query
  const { fromDate, toDate } = useMemo(() => {
    const now = new Date();
    const days = TIMEFRAME_WINDOW_DAYS[config.timeframe];
    const from = new Date(now);
    from.setDate(from.getDate() - days);
    return { fromDate: from, toDate: now };
  }, [config.timeframe]);

  const {
    data: priceData,
    isLoading,
    error,
  } = useQuery(
    getHistoricalPriceData,
    { symbol: config.symbol, date: fromDate, toDate, interval: config.timeframe },
    { enabled: !!config.symbol }
  );

  // Price precision — derived from first bar's open price
  const pricePrecision = useMemo(() => {
    if (!priceData || priceData.length === 0) return 5;
    const firstOpen = Number(priceData[0].open);
    return isNaN(firstOpen) ? 5 : firstOpen < 10 ? 5 : 2;
  }, [priceData]);

  const containerRef = useRef<HTMLDivElement>(null);
  const { seriesRef, seriesVersion } = useChartEngine(containerRef, priceData ?? [], {
    isDark,
    pricePrecision,
    themeId,
  });

  const candleTimes = useMemo(
    () => (priceData ?? []).map((d) => d.time as number).sort((a, b) => a - b),
    [priceData]
  );

  // Unique symbols from user's trades (for the picker "Mes trades" section)
  const tradeSymbols = useMemo(
    () => Array.from(new Set((trades ?? []).map((tr: any) => tr.symbol).filter(Boolean))),
    [trades]
  );

  // Trades matching the widget symbol
  const matchingTrades = useMemo(() => {
    if (!config.symbol) return [];
    const normalized = config.symbol.toLowerCase().replace(/=x$/i, "").replace(/-usd$/i, "").replace(/=f$/i, "");
    return (trades ?? []).filter((tr: any) => {
      const ts = (tr.symbol ?? "").toLowerCase().replace(/=x$/i, "").replace(/-usd$/i, "").replace(/=f$/i, "");
      return ts === normalized;
    });
  }, [trades, config.symbol]);

  useTradeMarkers(
    seriesRef,
    matchingTrades,
    config.symbol,
    config.showTrades,
    candleTimes,
    seriesVersion
  );

  const showEmpty =
    !isLoading &&
    (!priceData || priceData.length === 0) &&
    !error &&
    !!config.symbol;
  const showError = !!error;

  return (
    <Card className="flex flex-col h-full overflow-hidden">
      <ChartToolbar
        symbol={config.symbol}
        timeframe={config.timeframe}
        showTrades={config.showTrades}
        locked={config.locked}
        hasMatchingTrades={matchingTrades.length > 0}
        tradeSymbols={tradeSymbols}
        onSymbolChange={handleSymbolChange}
        onTimeframeChange={handleTimeframeChange}
        onToggleTrades={handleToggleTrades}
        onToggleLock={handleToggleLock}
      />

      <div className="flex-1 relative min-h-0">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>
        )}
        {showError && (
          <div className="absolute inset-0 flex items-center justify-center text-destructive text-sm px-4 text-center">
            {t.chart.invalidSymbol}
          </div>
        )}
        {showEmpty && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm px-4 text-center">
            {t.chart.noData}
          </div>
        )}
        {!config.symbol && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm px-4 text-center">
            {t.chart.symbol}…
          </div>
        )}
        <ChartErrorBoundary>
          <div ref={containerRef} className="w-full h-full" />
        </ChartErrorBoundary>
      </div>
    </Card>
  );
}
